import { createClient, SupabaseClient, PostgrestResponse, PostgrestSingleResponse, PostgrestError } from '@supabase/supabase-js';
// import { toast } from '@/components/ui/use-toast'; // Entfernt

/**
 * Configuration for the Supabase Client
 */
export type SupabaseConfig = {
  url: string;
  anonKey: string;
  storageKey?: string;
  autoRefreshToken?: boolean;
  persistSession?: boolean;
  timeoutMs?: number;
  maxRetries?: number;
};

// Global instance of the Supabase client (for frontend use with anon key)
let supabaseInstance: SupabaseClient | null = null;

// Connection status (for frontend client)
let connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error' = 'disconnected';
let lastConnectionError: unknown = null;

// Global variable for Supabase instance (set after initialization)
// export let supabase: SupabaseClient; // Less directly used

/**
 * Initializes the Supabase client for frontend use with the provided parameters.
 * This function is async and waits for the connection test to complete.
 */
export async function initializeSupabase(config: SupabaseConfig): Promise<SupabaseClient> {
  
  if (supabaseInstance) {
    return supabaseInstance;
  }

  if (!config.url || !config.anonKey) {
    console.error('[DEBUG] Supabase URL or Anon Key missing in configuration!');
    console.error('[DEBUG] URL:', config.url);
    console.error('[DEBUG] AnonKey:', config.anonKey ? 'SET' : 'MISSING');
    throw new Error('Invalid Supabase configuration');
  }

  
  try {
    connectionStatus = 'connecting';
    
    const instance = createClient(config.url, config.anonKey, {
      auth: {
        storageKey: config.storageKey || 'novel-scribe-auth-token',
        persistSession: config.persistSession !== undefined ? config.persistSession : true, 
        autoRefreshToken: config.autoRefreshToken !== undefined ? config.autoRefreshToken : true,
        detectSessionInUrl: true,
        flowType: 'pkce'
      },
      db: {
        schema: 'public'
      },
      global: {
        // IMPORTANT: Do NOT set any global headers.
        // Custom headers can trigger failing CORS preflights on Supabase Storage endpoints.
        fetch: (url, options = {}) => {
          // Increase timeout for storage operations, especially uploads
          const urlString = typeof url === 'string' ? url : url.toString();
          const isStorageRequest = urlString.includes('/storage/v1/');
          const isAuthRequest = urlString.includes('/auth/v1/');
          const timeout = isStorageRequest || isAuthRequest ? 120000 : 45000; // 2 minutes for storage/auth, 45s for others
          
          // Keep Supabase Auth requests untouched to avoid interfering with PKCE/session exchange internals.
          if (isAuthRequest) {
            return fetch(url, options);
          }

          // Do NOT wrap non-GET Storage requests (uploads) to avoid interfering with streaming/CORS
          try {
            const requestOptions = options as RequestInit | undefined;
            const method = requestOptions?.method?.toString().toUpperCase() || 'GET';
            if (isStorageRequest && method !== 'GET') {
              return fetch(url, options);
            }
          } catch {
            // Fall back to wrapped fetch when method detection fails.
          }

          return new Promise((resolve, reject) => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
              controller.abort();
              reject(new Error(`Request timeout after ${timeout}ms`));
            }, timeout);
            
            fetch(url, {
              ...options,
              signal: controller.signal,
            })
            .then(response => {
              clearTimeout(timeoutId);
              resolve(response);
            })
            .catch(error => {
              clearTimeout(timeoutId);
              if (error.name === 'AbortError') {
                reject(new Error(`Request timeout after ${timeout}ms`));
              } else {
                reject(error);
              }
            });
          });
        }
        //   const timeout = config.timeoutMs || 30000; // 30 seconds
        //   
        //   const controller = new AbortController();
        //   const id = setTimeout(() => {
        //     console.warn('Supabase request timed out after', timeout, 'ms');
        //     controller.abort();
        //   }, timeout);
        //   
        //   // Ensure options is an object
        //   const safeOptions = options || {};
        //   safeOptions.signal = controller.signal;
        //   
        //   return fetch(url, safeOptions)
        //     .then(response => {
        //       clearTimeout(id);
        //       return response;
        //     })
        //     .catch(error => {
        //       clearTimeout(id);
        //       throw error;
        //     });
        // }
        // --- END TEMPORARY DISABLE ---
      }
    });

    // Make the client available immediately so app bootstrap cannot get stuck forever.
    // Connection checks run as best-effort in background and must not block initial render.
    supabaseInstance = instance;

    const isOAuthCallbackRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/auth/callback') &&
      new URLSearchParams(window.location.search).has('code');

    if (isOAuthCallbackRoute) {
      // Avoid competing auth calls during PKCE code exchange on callback route.
      // The callback page/AuthContext will resolve auth state explicitly.
      connectionStatus = 'connected';
    } else {
      // Best-effort connection check with hard timeout; never block bootstrap on it.
      const connectionTestTimeoutMs = config.timeoutMs ?? 8000;
      void Promise.race<boolean>([
        testConnectionInternal(instance),
        new Promise<boolean>((resolve) => setTimeout(() => resolve(false), connectionTestTimeoutMs)),
      ])
        .then((connectionSuccessful) => {
          if (connectionSuccessful) {
            connectionStatus = 'connected';
            return;
          }

          // Keep instance usable; downstream code can retry/handle transient backend issues.
          connectionStatus = 'error';
          if (import.meta.env.DEV) {
            console.warn('[supabase-client] Initial connection test timed out or failed. Continuing with initialized client.');
          }
        })
        .catch((error) => {
          connectionStatus = 'error';
          lastConnectionError = error;
        });
    }

    // Add listener for auth changes only after successful initialization
    supabaseInstance.auth.onAuthStateChange((event, session) => {
    });
    
    return supabaseInstance;

  } catch (error) {
    connectionStatus = 'error';
    lastConnectionError = error;
    console.error('[supabase-client] Error initializing the Supabase client:', error);
    // Re-throw error so main.tsx can catch it
    throw error;
  }
}

/**
 * Internal function to test the connection.
 * Called by initializeSupabase.
 */
async function testConnectionInternal(client: SupabaseClient): Promise<boolean> {
  try {
    const { data, error } = await client.auth.getSession(); // Use provided client
    
    if (error) {
      // Set global status, but don't handle error finally here
      connectionStatus = 'error'; 
      lastConnectionError = error;
      console.error('[supabase-client] Supabase connection test failed:', error);
      return false;
    }
    
    // Don't set status here, as the instance is not yet global
    // connectionStatus = 'connected'; 
    return true;
  } catch (error) {
    connectionStatus = 'error';
    lastConnectionError = error;
    console.error('[supabase-client] Unexpected error testing Supabase connection:', error);
    return false;
  }
}

// The public testConnection function checks if the client is initialized
export async function testConnection(): Promise<boolean> {
  if (!supabaseInstance) {
    console.error('[supabase-client] Supabase client not yet initialized for testConnection!');
    return false;
  }
  // Call the internal function with the global instance
  return testConnectionInternal(supabaseInstance);
}

/**
 * Returns the current connection status
 */
export function getConnectionStatus(): { status: string; error: unknown } {
  return {
    status: connectionStatus,
    error: lastConnectionError
  };
}

/**
 * Returns the current instance of the Supabase client.
 * Important: This function should only be called AFTER initializeSupabase was successful.
 */
export function getSupabaseClient(): SupabaseClient {
  if (!supabaseInstance) {
    console.error('[DEBUG] getSupabaseClient called, but instance is null! Initialization failed or not yet completed?');
    throw new Error('Supabase client not initialized');
  }
  return supabaseInstance;
}

// Export the current Supabase instance for easy access via getter
// under the name `supabaseClient`.
export const supabaseClient = {
  get client(): SupabaseClient {
    if (!supabaseInstance) {
      console.error('[supabase-client] Access to supabaseClient.client, but instance is null! Initialization failed or not yet completed?');
      // In a browser context, it might be better not to throw an error here,
      // but to signal a state or wait for initialization.
      // For now, we keep the error to detect problems early.
      throw new Error('Supabase client not yet explicitly initialized!');
    }
    return supabaseInstance;
  }
};

// Type definition for a request function that returns an array
export type RequestArrayFunction<T> = () => Promise<PostgrestResponse<T[]>>;
// Type definition for a request function that returns a single object
export type RequestSingleFunction<T> = () => Promise<PostgrestSingleResponse<T>>;

// --- Supabase Request Wrapper --- 

// Type definitions for the wrappers
export interface SupabaseRequestOptions {
  maxRetries?: number;
  initialDelay?: number;
  timeoutMs?: number;
  retryOnNotFound?: boolean; // Whether to retry on 404 (PGRST116)
  expectNullData?: boolean; // NEW: Does the request explicitly expect null as data on success? (e.g., for DELETE)
  isSingleRecord?: boolean; // Internal flag for logging
  cacheTtlMs?: number; // Opt-in response cache TTL for successful requests
  notFoundCacheTtlMs?: number; // Opt-in cache TTL for "not found" responses
  errorCacheTtlMs?: number; // Opt-in cache TTL for errors
  // showToast?: boolean; // Entfernt
}

export interface SupabaseRequestResult<T> {
  data: T | null;
  error: PostgrestError | Error | null;
}

// Specific result types for clarity
export type SupabaseRequestSingleResult<T> = SupabaseRequestResult<T | null>;
// Corrected type definition: The data field can be T[] or null
export type SupabaseRequestArrayResult<T> = SupabaseRequestResult<T[]>;
// NEW: Type for requests without return data (e.g., delete, insert without select)
export type SupabaseRequestVoidResult = { data: null; error: PostgrestError | Error | null }; // Data is always null

// Cache für laufende Anfragen zur Deduplizierung
const pendingRequests = new Map<string, Promise<unknown>>();

/**
 * Deduplicates API requests with the same cache key and prevents race conditions
 * @param cacheKey A unique key for this request
 * @param requestFn The request function to execute
 * @param expiryMs How long to keep the request in cache (in ms)
 * @returns The result of the request
 */
export async function deduplicatedRequest<T>(
  cacheKey: string, 
  requestFn: () => Promise<T>, 
  expiryMs: number = 5000
): Promise<T> {
  // Prüfen, ob bereits eine identische Anfrage läuft
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey) as Promise<T>;
  }
  
  // Anfrage ausführen und im Cache speichern
  const promise = requestFn().finally(() => {
    // Nach Ablauf der expiryMs die Anfrage aus dem Cache entfernen
    setTimeout(() => {
      pendingRequests.delete(cacheKey);
    }, expiryMs);
  });
  
  pendingRequests.set(cacheKey, promise);
  return promise;
}


// Optionaler In-Memory-Cache für Supabase-Abfragen (opt-in per request options).
interface CacheEntry<T> {
  data: T | null;
  error: PostgrestError | Error | null;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry<unknown>>();

/**
 * Invalidates cache entries that match a pattern
 * @param pattern - String pattern to match against cache keys
 * This allows more flexible invalidation.
 */
export function invalidateCache(pattern: string): void {
  // Invalidate the cache for completed requests
  const cachedKeysToDelete = Array.from(cache.keys()).filter(key => key.includes(pattern));
  
  if (cachedKeysToDelete.length > 0) {
    cachedKeysToDelete.forEach(key => cache.delete(key));
  }

  // Also, invalidate the cache for pending (in-flight) requests to prevent
  // returning stale data from a request that was initiated *before* the invalidation.
  const pendingKeysToDelete = Array.from(pendingRequests.keys()).filter(key => key.includes(pattern));
  
  if (pendingKeysToDelete.length > 0) {
    pendingKeysToDelete.forEach(key => pendingRequests.delete(key));
  }
}

/**
 * Retrieves an entry from the cache if it exists and is not expired.
 */
export function getCacheEntry<T>(cacheKey: string): CacheEntry<T> | undefined {
  const entry = cache.get(cacheKey);
  if (entry && entry.expiresAt > Date.now()) {
    return entry as CacheEntry<T>;
  }
  if (entry) {
    cache.delete(cacheKey);
  }
  return undefined;
}

const SESSION_RECOVERY_TIMEOUT_MS = 8000;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return String((error as { message: string }).message);
  }
  return '';
};

const isSessionRecoveryError = (error: unknown): boolean => {
  const message = getErrorMessage(error).toLowerCase();
  if (!message) return false;

  return (
    message.includes('timed out') ||
    message.includes('timeout') ||
    message.includes('failed to fetch') ||
    message.includes('network') ||
    message.includes('connection') ||
    message.includes('unauthorized') ||
    message.includes('jwt') ||
    message.includes('token') ||
    message.includes('auth') ||
    message.includes('session')
  );
};

const refreshSessionForRecovery = async (): Promise<void> => {
  if (!supabaseInstance) return;

  await Promise.race([
    supabaseInstance.auth.refreshSession(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Session recovery refresh timed out')), SESSION_RECOVERY_TIMEOUT_MS)
    ),
  ]);
};

export const supabaseRequest = async <T>(
  requestFn: () => Promise<PostgrestResponse<T> | PostgrestSingleResponse<T>>,
  errorMessage: string,
  options: SupabaseRequestOptions = {}
): Promise<SupabaseRequestResult<T>> => {
  const { 
    maxRetries = 3, 
    initialDelay = 500, 
    timeoutMs = 30000, 
    retryOnNotFound = false,
    expectNullData = false,
    isSingleRecord = false,
    cacheTtlMs = 0,
    notFoundCacheTtlMs = 0,
    errorCacheTtlMs = 0,
    // showToast = true // Entfernt
  } = options;

  const cacheKey = errorMessage; // Use only errorMessage as cache key for explicit invalidation patterns
  const isCachingEnabled = cacheTtlMs > 0 || notFoundCacheTtlMs > 0 || errorCacheTtlMs > 0;
  const cachedResult = isCachingEnabled ? getCacheEntry<T>(cacheKey) : undefined;

  if (cachedResult) {
    return { data: cachedResult.data, error: cachedResult.error as PostgrestError | Error | null };
  }

  let attempt = 1;
  let lastError: PostgrestError | Error | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let sessionRecoveryAttempted = false;

  const maybeRecoverSession = async (error: unknown) => {
    if (sessionRecoveryAttempted || !isSessionRecoveryError(error)) return;
    sessionRecoveryAttempted = true;

    try {
      await refreshSessionForRecovery();
    } catch (sessionRecoveryError) {
      console.warn('[supabaseRequest] Session recovery failed:', sessionRecoveryError);
    }
  };

  while (attempt <= maxRetries) {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    try {
      
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          const error = new Error(`Request timeout after ${timeoutMs}ms: ${errorMessage}`);
          reject(error);
        }, timeoutMs);
      });

      let response: { data: T | null; error: PostgrestError | null } | null = null;
      let raceError: Error | PostgrestError | null = null;

      try {
        const requestPromise = requestFn(); // requestFn returns a Promise
        const result = await Promise.race([requestPromise, timeoutPromise]);
        
        if (result && typeof result === 'object' && 'data' in result) { // Error might not be present on success
          response = result as { data: T | null; error: PostgrestError | null };
        } else if (result && typeof result === 'object' && !('data' in result) && 'message' in result && 'code' in result) {
          // This case handles if PostgrestError itself is returned by requestFn in some edge cases
           response = { data: null, error: result as PostgrestError};
        } else {
          // If result is not in expected shape, it might be an error from timeoutPromise or other unexpected structure
          // This branch will likely be caught by the outer catch if 'result' is an error itself.
          // If it's an unexpected success shape, we might need to adapt.
          // For now, assume if not fitting the success shape, it's an issue.
        }
        
      } catch (e) {
        raceError = e instanceof Error ? e : new Error(String(e));
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }

      if (raceError) {
        await maybeRecoverSession(raceError);
        const retryDelay = initialDelay * Math.pow(1.5, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        lastError = raceError;
        attempt++;
        continue;
      }

      if (response) {
        if (response.error) {
          const isNotFoundError = response.error.code === "PGRST116" || 
                                response.error.message?.includes("Results contain 0 rows") || 
                                response.error.message?.includes("JSON object requested, multiple");

          if (isNotFoundError && !retryOnNotFound) {
            if (notFoundCacheTtlMs > 0) {
              cache.set(cacheKey, {
                data: null,
                error: null,
                expiresAt: Date.now() + notFoundCacheTtlMs
              });
            }
            return { data: null, error: null }; // Return null data, null error for "not found" when not retrying
          } else if (isNotFoundError && retryOnNotFound && attempt < maxRetries) {
            const retryDelay = initialDelay * Math.pow(1.5, attempt - 1);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            lastError = response.error;
            attempt++;
            continue;
          } else {            
            lastError = response.error;
            // if (attempt >= maxRetries && showToast) { // Entfernt
            //   console.error(`[supabaseRequest${isSingleRecord ? 'Single' : 'Array'}] Error after ${attempt} attempts: ${response.error.message || 'Unknown error'}`);
            // }
            
            if (attempt < maxRetries) {
              await maybeRecoverSession(response.error);
              const retryDelay = initialDelay * Math.pow(1.5, attempt - 1);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              attempt++;
              continue; // Added continue here
            } else {
              // All retries exhausted for this specific error type
              // console.error(`[supabaseRequest${isSingleRecord ? 'Single' : 'Array'}] Error after ${attempt} attempts: ${response.error.message || 'Unknown error'}`);
              if (errorCacheTtlMs > 0) {
                cache.set(cacheKey, {
                  data: null,
                  error: response.error,
                  expiresAt: Date.now() + errorCacheTtlMs
                });
              }
              return { data: null, error: response.error };
            }
          }
        } else {
          // Success
          if (cacheTtlMs > 0) {
            cache.set(cacheKey, {
              data: response.data,
              error: null,
              expiresAt: Date.now() + cacheTtlMs
            });
          }
          return { data: response.data as T, error: null }; 
        }
      } else {
        // console.error(`[supabaseRequest${isSingleRecord ? 'Single' : 'Array'}] Attempt ${attempt}/${maxRetries} UNEXPECTED STATE: No response and no raceError.`);
        lastError = new Error('Internal error: No response data after Promise.race');
        if (attempt >= maxRetries) break;
        const delay = initialDelay * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
      }

    } catch (outerError) {
      if (timeoutId) clearTimeout(timeoutId);
      if (outerError instanceof Error) {
        lastError = outerError;
      } else if (outerError && typeof outerError === 'object' && 'message' in outerError) {
        lastError = outerError as PostgrestError;
      } else {
        lastError = new Error(String(outerError));
      }
      // console.error(`[supabaseRequest${isSingleRecord ? 'Single' : 'Array'}] Critical error during retry loop for "${errorMessage}"`);
      break; 
    }
  }

  // console.error(`[supabaseRequest${isSingleRecord ? 'Single' : 'Array'}] All ${maxRetries} attempts failed for "${errorMessage}"`);
  // if (showToast) { // Entfernt
  //   const finalErrorMessage = lastError instanceof Error ? `${errorMessage}: ${lastError.message}` : errorMessage;
  //   // toast({ title: "Error", description: finalErrorMessage, variant: "destructive" }); // Entfernt
  // }
  if (errorCacheTtlMs > 0) {
    cache.set(cacheKey, {
      data: null,
      error: lastError,
      expiresAt: Date.now() + errorCacheTtlMs
    });
  }
  return { data: null, error: lastError };
};


export const supabaseRequestSingle = async <T>(
  requestFn: RequestSingleFunction<T>,
  failureMessage: string,
  options: SupabaseRequestOptions = {}
): Promise<SupabaseRequestSingleResult<T>> => {
  return supabaseRequest<T>(
    requestFn as () => Promise<PostgrestSingleResponse<T>>, 
    failureMessage, 
    { ...options, isSingleRecord: true }
  );
};

export const supabaseRequestArray = async <T>(
  requestFn: RequestArrayFunction<T>, 
  failureMessage: string,
  options: SupabaseRequestOptions = {}
): Promise<SupabaseRequestArrayResult<T>> => {
  return supabaseRequest<T[]>( // Specify T[] here
    requestFn as () => Promise<PostgrestResponse<T[]>>, 
    failureMessage, 
    options
  ) as Promise<SupabaseRequestArrayResult<T>>; // Cast the result
};


export type RequestVoidFunction = () => Promise<{ data: null; error: PostgrestError | null }>;

export const supabaseRequestVoid = async (
  requestFn: RequestVoidFunction, 
  errorMessage: string,
  options: SupabaseRequestOptions = {}
): Promise<SupabaseRequestVoidResult> => {
  const { 
    maxRetries = 2, 
    initialDelay = 1000, 
    timeoutMs = 20000,
    // showToast = true // Entfernt
  } = options;

  let attempt = 0;
  while (attempt <= maxRetries) {
    try {
      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error(`Request timed out: ${errorMessage}`)), timeoutMs)
      );

      // Race the request function against the timeout
      const result = await Promise.race([requestFn(), timeoutPromise]);
      
      // Check for Supabase error in the result
      if (result.error) {
        console.warn(`[supabaseRequestVoid] Attempt ${attempt + 1} failed for "${errorMessage}": ${result.error.message}`);
        throw result.error; // Throw to trigger retry
      }
      
      // If no error, the void operation was successful
      return { data: null, error: null };

    } catch (error: unknown) {
      attempt++;
      if (attempt > maxRetries) {
        console.error(
          `[supabaseRequestVoid] All ${maxRetries + 1} retries failed for "${errorMessage}". Last error: ${getErrorMessage(error) || String(error)}`
        );
        // if (showToast) { // Entfernt
        //   // toast({ title: 'Fehler', description: `${errorMessage}: ${error.message || 'Maximale Wiederholungsversuche erreicht.'}`, variant: 'destructive' }); // Entfernt
        // }
        return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
      }
      await new Promise(resolve => setTimeout(resolve, initialDelay * Math.pow(2, attempt - 1)));
    }
  }
  // Should not be reached if logic is correct
  return { data: null, error: new Error(`Unexpected failure for ${errorMessage} after all retries.`) };
};


export async function getCurrentSession() {
  if (!supabaseInstance) {
    console.warn("[supabase-client] getCurrentSession called before Supabase was initialized.");
    return null;
  }
  const { data, error } = await supabaseInstance.auth.getSession();
  if (error) {
    console.error("[supabase-client] Error getting current session:", error);
    return null;
  }
  return data.session;
}

export async function getCurrentUser() {
  if (!supabaseInstance) {
    console.warn("[supabase-client] getCurrentUser called before Supabase was initialized.");
    return null;
  }
  const { data: { user }, error } = await supabaseInstance.auth.getUser();
  if (error) {
    console.error("[supabase-client] Error getting current user:", error);
    return null;
  }
  return user;
}

// Example of a more specific data fetching function
// export async function fetchUserProfile(userId: string) {
//   return supabaseRequestSingle(
//     () => supabaseClient.client.from('profiles').select('*').eq('id', userId).single(),
//     'Failed to fetch user profile'
//   );
// } 

/**
 * SECURITY: Admin client function removed for client-side safety.
 * Service Role Keys must NEVER be exposed to client-side code.
 * Use Supabase Edge Functions or your own API for admin operations.
 */
export function createSupabaseAdminClient(): never {
  throw new Error('🚨 SECURITY: Admin client creation is not allowed in client-side code. Use Supabase Edge Functions or server-side API for admin operations.');
} 
